import React from 'react';
import {observer} from "mobx-react-lite";
import {useAppState} from "@stores/AppStateContext";

import type {ItemModelType} from "@stores/WorkspaceStore";

import {useCrudServices} from "../../../platform/hooks/useServiceCombinations";
import {SharedDynamicViewVariation} from "../ListView/SharedDynamicViewVariation";
import {DocumentTreeSource, shortenId} from '../ListView/DocumentTreeSource';
import {ClusterEmpty} from "./ClusterEmpty";


import './ClusterView.css';
import {RootStoreModel} from "@stores/RootStore";


export interface ClusterViewProps {
    item: ItemModelType;
}

export const ClusterView = observer(({item}: ClusterViewProps) => {
    const appState = useAppState();
    const crudServices = useCrudServices();
    const rootStore = appState.rootStore;
    const activeWorkspace = rootStore.view.workspace!;
    const parentPaths = item.getParentPaths();
    const renderingRootId = buildTreePath(rootStore, parentPaths, item);

    const treeSource = new DocumentTreeSource({crudServices, appState}, activeWorkspace);

    return (
        <div
            key={rootStore.currentLocale}
            className='ClusterView'
        >
            <div className='ClusterView_tree_wrap'>
                {
                    (item.childIds.length === 0)
                        ? <ClusterEmpty item={item}/>
                        : <SharedDynamicViewVariation treeSource={treeSource}
                                                      inEditorView={true}
                                                      renderingRootId={renderingRootId}/>
                }
            </div>
        </div>
    )
});


const buildTreePath = (rootStore: RootStoreModel, parentPaths: string[][], item: ItemModelType) => {
    const wsMST = rootStore.view.workspace!;
    const view = wsMST.view;
    let rootIds: string[] = [];
    switch (view) {
        case 'archive': {
            rootIds = wsMST.archivedIds.toJSON();
            break;
        }
        case 'trash': {
            rootIds = wsMST.trashedIds.toJSON();
            break;
        }
        case 'main': {
            rootIds = wsMST.childIds.toJSON();
            break;
        }
    }
    if (rootIds.includes(item.id)) {
        return `${view}/${shortenId(item.id)}`;
    }
    let parentPath: string[] = [];
    if (parentPaths.length > 0) {
        let shortestPathIndex = 0; // matters when recursion present in the hierarchy
        for (let i = 1; i < parentPaths.length; i++) {
            const _path = parentPaths[i];
            if (_path.includes(item.id)) {
                // no recursion in visible part of the tree
                continue;
            }
            if (_path.length < parentPaths[shortestPathIndex].length) {
                shortestPathIndex = i;
            }
        }
        parentPath = parentPaths[shortestPathIndex];
        return view + '/' + parentPath.map(itemId => shortenId(itemId)).join('/') + '/' + shortenId(item.id);
    } else {
        return view + '/' + shortenId(item.id);
    }
}
